import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { validationErrorMessage } from "../lib/validation-error-message";
import type { FormErrors } from "@repo/shared-types";

interface UseFormProps<T> {
  initialValues: T;
  customValidate?: (values: Partial<T>) => FormErrors;
  onSubmit?: (e: SubmitEvent) => void;
}

export const useForm = <T>({
  initialValues,
  customValidate,
  onSubmit,
}: UseFormProps<T>) => {
  const ref = useRef<HTMLFormElement>(null);
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const customErrors = useMemo(
    () => (customValidate ? customValidate(values) : {}),
    [customValidate, values]
  );

  useEffect(() => {
    if (!ref.current) return;
    const form = ref.current;
    form.noValidate = true;
    const formElements = Array.from(form.elements) as (
      | HTMLInputElement
      | HTMLSelectElement
    )[];
    formElements.forEach((input) => {
      if (input.type === "checkbox") {
        input.checked = values[input.name as keyof T] as boolean;
      } else {
        const currentValue = values[input.name as keyof T];
        if (currentValue) {
          input.value = currentValue as string;
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValidate = useCallback((target: Element) => {
    if (
      !(
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement
      )
    )
      return;
    const { validity, name } = target;
    if (validity.valid) {
      setErrors((prev: FormErrors) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setErrors((prev: FormErrors) => ({
        ...prev,
        [name]: {
          message: validationErrorMessage(validity, name),
        },
      }));
    }
  }, []);

  const handleInputChange = useCallback(
    (e: Event) => {
      const target = e.target as HTMLInputElement;
      const { name, value } = target;
      if (target.type === "checkbox") {
        setValues((prev) => ({ ...prev, [name]: target.checked }));
      } else {
        setValues((prev) => ({ ...prev, [name]: value }));
      }
      handleValidate(target);
    },
    [handleValidate, setValues]
  );

  const handleSubmit = useCallback(
    async (e: SubmitEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setSubmitted(true);
      if (!ref.current) return;
      const form = ref.current;
      if (!form.checkValidity()) {
        const invalidField = form.querySelectorAll(
          ":invalid"
        )[0] as HTMLInputElement;
        if (invalidField) {
          invalidField.focus();
        }
        Array.from(form.elements).forEach(handleValidate);
        return;
      } else {
        setErrors({});
      }
      if (Object.keys(customErrors).length > 0) {
        return;
      }
      await onSubmit?.(e);
    },
    [customErrors, handleValidate, onSubmit]
  );

  const handleChangeValue = useCallback(
    (key: string, value: any) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [handleValidate, setValues]
  );

  useEffect(() => {
    if (!ref.current) return;
    const form = ref.current;

    form.addEventListener("input", handleInputChange);
    form.addEventListener("submit", handleSubmit);
    return () => {
      if (!form) return;
      form.removeEventListener("input", handleInputChange);
      form.removeEventListener("submit", handleSubmit);
    };
  }, [handleInputChange, handleSubmit, ref]);

  return {
    values,
    errors: submitted ? { ...errors, ...customErrors } : ({} as FormErrors),
    ref,
    handleChangeValue,
  };
};
